/**
 * <ThinkingDots /> — the "Solvait is thinking…" indicator that
 * shows whenever a chat turn is streaming but hasn't yet
 * produced any text the user can read.
 *
 * The chat orchestrator chains multiple tool calls per turn —
 * `query_employees → query_contracts → explain_override_stack →
 * synthesis text`. Between any two of those calls, Claude is
 * actively planning the next step but nothing is rendered in
 * the bubble. Without this indicator the chat looks frozen
 * for the 1–10 seconds between tool completion and the next
 * SSE event.
 *
 * Three dots that pulse out of phase. Subtle (small + low
 * opacity) so it doesn't compete with the actual tool-result
 * chips above it. Disappears the moment streamed text starts
 * to arrive.
 */

import { useTranslation } from "react-i18next";

export function ThinkingDots() {
  const { t } = useTranslation();
  return (
    <div
      className="flex items-center gap-1 text-xs text-text-tertiary"
      role="status"
      aria-live="polite"
      aria-label={t("app.cs_thinking")}
    >
      <span>{t("app.chat_working")}</span>
      <span aria-hidden="true" className="inline-flex items-end gap-0.5">
        <Dot delayMs={0} />
        <Dot delayMs={200} />
        <Dot delayMs={400} />
      </span>
    </div>
  );
}

function Dot({ delayMs }: { delayMs: number }) {
  return (
    <span
      className="inline-block h-1 w-1 rounded-full bg-current motion-safe:animate-thinking-dot"
      style={{ animationDelay: `${delayMs}ms` }}
    />
  );
}
